import axios from 'axios';
import { apiClient } from 'services/api/apiClient';

/**
 * 현장 설정 파일 — docker/deploy/sites/<코드>/ 의 router.env·lights.toml 등.
 * 백엔드(deployment/siteConfig)가 GitHub 저장소를 읽고, 저장은 작업 브랜치 → PR → 배포 브랜치 머지로 한다.
 * 저장만 하고 반영은 배포 때 — 배포 워크플로가 이 폴더를 복사해 쓴다.
 */
export interface SiteConfigFile {
  name: string;
  label: string;
  /** site = 현장 통합서버, company = 사내 테스트 서버(102) */
  scope: 'site' | 'company';
  exists: boolean;
  size: number;
  /** 저장소 blob sha — 현장 실제 파일(SiteLiveConfigFile.sha)과 비교한다. 없는 파일은 없음. */
  sha?: string;
}

export interface SiteConfigCommit {
  sha: string;
  message: string;
  author: string;
  date: string;
  html_url: string;
}

/** 방재실 하나의 설정 폴더. problem 이 있으면 폴더를 쓸 수 없다(코드 미지정 등). */
export interface SiteConfigSite {
  room: string;
  code: string;
  problem?: string;
  path?: string;
  folderExists: boolean;
  lastCommit?: SiteConfigCommit;
  files: SiteConfigFile[];
}

export interface SiteConfigList {
  branch: string;
  /** 저장소에 있는 현장 폴더 이름 — 설정 코드가 어긋났을 때 보기로 보여 준다. */
  folders: string[];
  sites: SiteConfigSite[];
}

export interface SiteConfigContent {
  code: string;
  name: string;
  path: string;
  exists: boolean;
  content: string;
  sha: string;
}

/** 서버가 준 사유와 상태 코드 — 409 는 "그사이 다른 사람이 고쳤다". */
export class SiteConfigError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'SiteConfigError';
  }
}

interface Envelope<T> {
  success: boolean;
  message?: string;
  data?: T;
}

async function call<T>(run: () => Promise<{ data: Envelope<T> }>, fallback: string): Promise<T> {
  try {
    const res = await run();
    if (!res.data.success || res.data.data === undefined) throw new SiteConfigError(res.data.message || fallback);
    return res.data.data;
  } catch (e) {
    if (e instanceof SiteConfigError) throw e;
    if (axios.isAxiosError(e)) {
      throw new SiteConfigError((e.response?.data as Envelope<T> | undefined)?.message || fallback, e.response?.status);
    }
    throw new SiteConfigError(fallback);
  }
}

const base = (projectId: number) => `/v0.1/deployments/projects/${projectId}/site-config`;
const filePath = (projectId: number, code: string, name: string) =>
  `${base(projectId)}/${encodeURIComponent(code)}/${encodeURIComponent(name)}`;

export const SiteConfigService = {
  list(projectId: number): Promise<SiteConfigList> {
    return call(() => apiClient.get<Envelope<SiteConfigList>>(base(projectId)), '설정 파일 목록을 읽지 못했습니다');
  },
  get(projectId: number, code: string, name: string): Promise<SiteConfigContent> {
    return call(() => apiClient.get<Envelope<SiteConfigContent>>(filePath(projectId, code, name)), '설정 파일을 읽지 못했습니다');
  },
  save(
    projectId: number,
    code: string,
    name: string,
    body: { content: string; sha: string; message: string },
  ): Promise<SiteConfigCommit> {
    return call(
      () => apiClient.put<Envelope<SiteConfigCommit>>(filePath(projectId, code, name), body),
      '설정 파일을 저장하지 못했습니다',
    );
  },
};
